import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon, ArrowRightWhiteIcon, CloseIcon } from '../icons';
import { apiService } from '../../services/apiService';
import { friendlyAuthError } from '../../utils/authErrors';
import { InlineAlert } from '../InlineAlert';

interface ForgotPasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSetPassword: (newPassword: string) => void;
    title?: string; // Optional custom title
    description?: string; // Optional custom description
    /** Forgot-password: the one-time token the reset code returned. With it
     *  the password is reset for that e-mail's account while signed out;
     *  without it the signed-in user's own password is set. */
    resetToken?: string;
    /** The address being reset, shown so the person knows which account. */
    resetEmail?: string;
}

const PasswordInput = ({ label, placeholder, value, onChange, showPassword, toggleShowPassword }: { label: string; placeholder: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; showPassword: boolean; toggleShowPassword: () => void; }) => (
    <div className="flex flex-col items-start gap-1.5 flex-1 w-full">
        <label className="text-grayscale-1100 font-grotesk text-xs">{label}</label>
        <div className="flex h-11 py-3 px-2.5 items-center gap-2.5 self-stretch rounded-md bg-grayscale-200">
            <input
                type={showPassword ? 'text' : 'password'}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                className="flex-1 bg-transparent text-grayscale-1300 font-grotesk text-sm focus:outline-none placeholder:text-secondary-text"
            />
            <button type="button" onClick={toggleShowPassword}>
                {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
            </button>
        </div>
    </div>
);

export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ 
    isOpen, 
    onClose, 
    onSetPassword,
    title = "Set New Password",
    description = "Please enter and confirm your new password.",
    resetToken,
    resetEmail
}) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setNewPassword('');
            setConfirmPassword('');
            setError('');
            setIsValid(false);
            setIsLoading(false);
            setApiError('');
        }
    }, [isOpen]);

    useEffect(() => {
        const hasUpperCase = /[A-Z]/.test(newPassword);
        const hasNumber = /[0-9]/.test(newPassword);
        const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(newPassword);
        const isLongEnough = newPassword.length >= 8;
        const passwordsMatch = newPassword !== '' && newPassword === confirmPassword;

        if (newPassword && (!isLongEnough || !hasUpperCase || !hasNumber || !hasSymbol)) {
            setError('8+ characters, 1 capital letter, 1 number, and 1 symbol');
        } else if (newPassword && confirmPassword && !passwordsMatch) {
            setError('Passwords do not match');
        } else {
            setError('');
        }

        setIsValid(isLongEnough && hasUpperCase && hasNumber && hasSymbol && passwordsMatch);
    }, [newPassword, confirmPassword]);
    
    const handleConfirm = async () => {
        if (!isValid || isLoading) return;
        
        setIsLoading(true);
        setApiError('');
        
        try {
            if (resetToken) {
                // Forgot-password: signed out, so the reset token (issued when
                // the e-mailed code checked out) identifies the account.
                const response = await apiService.confirmPasswordReset(resetToken, newPassword);
                if (response.success) {
                    onSetPassword(newPassword);
                } else if (/reset token/i.test(response.error || '')) {
                    setApiError('This reset has expired. Close this window and request a new code.');
                } else {
                    setApiError(friendlyAuthError(response.error, 'Failed to set password. Please try again.'));
                }
                return;
            }

            // Signed in (e.g. setting a first password after sign-up).
            const userId = apiService.getStoredUserId();
            if (!userId) {
                setApiError('User session not found. Please try logging in again.');
                setIsLoading(false);
                return;
            }

            // Call the password set API
            const response = await apiService.setPassword(userId, newPassword);

            if (response.success) {
                // Call the original onSetPassword callback for any additional logic
                onSetPassword(newPassword);
                onClose();
            } else {
                setApiError(friendlyAuthError(response.error, 'Failed to set password. Please try again.'));
            }
        } catch (error) {
            setApiError('Network error. Please check your connection and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 w-full h-full flex justify-center items-center bg-black/70 backdrop-blur-sm z-50 p-4">
            <div className="relative rounded-2xl shadow-lg w-full max-w-md p-8 flex flex-col gap-4 bg-white font-grotesk">
                <button onClick={onClose} className="absolute top-4 right-4 p-2.5 rounded-full bg-grayscale-1200 text-white hover:bg-grayscale-1100 shadow-md">
                    <CloseIcon />
                </button>
                <h2 className="text-grayscale-1300 font-grotesk text-xl font-medium text-center">{title}</h2>
                <p className="text-sm text-grayscale-1100 mt-1 text-center">
                    {resetToken && resetEmail ? <>Choose a new password for <strong>{resetEmail}</strong>.</> : description}
                </p>
                
                <div className="flex flex-col gap-4 mt-4">
                    <div className="flex flex-col gap-1.5 w-full">
                         <PasswordInput 
                            label="Enter new password"
                            placeholder="********"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            showPassword={showNewPassword}
                            toggleShowPassword={() => setShowNewPassword(p => !p)}
                        />
                        <p className="text-primary-og-600 font-grotesk text-xs h-4">{error && newPassword.length > 0 ? error : '8+ characters, 1 capital letter, 1 number, and 1 symbol'}</p>
                    </div>
                    <div className="flex flex-col gap-1.5 w-full">
                         <PasswordInput 
                            label="Confirm new password"
                            placeholder="Re-enter new password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            showPassword={showConfirmPassword}
                            toggleShowPassword={() => setShowConfirmPassword(p => !p)}
                        />
                        {error && confirmPassword && newPassword !== confirmPassword && <p className="text-red-500 font-grotesk text-xs h-4">{error}</p>}
                    </div>
                </div>

                {/* API Error Message */}
                {apiError && (
                    <InlineAlert variant="error" className="mt-4">{apiError}</InlineAlert>
                )}

                <div className="flex items-center gap-3 self-stretch mt-4">
                     <button
                        onClick={handleConfirm}
                        disabled={!isValid || isLoading}
                        className={`flex py-2.5 px-4 justify-center items-center gap-2 flex-1 rounded-full shadow-custom-light transition-colors ${isValid && !isLoading ? 'bg-primary-600 text-white' : 'bg-primary-300 text-white cursor-not-allowed'}`}
                    >
                        <span className="text-center font-grotesk text-sm font-medium">
                            {isLoading ? 'Setting Password...' : 'Confirm'}
                        </span>
                        {!isLoading && <ArrowRightWhiteIcon />}
                    </button>
                </div>
            </div>
        </div>
    );
};
